import { useCurrentEditor } from "@tiptap/react";
import { type FC, type ReactNode, useMemo } from "react";
import { cn } from "../../../utils";
import { Button, type ButtonProps } from "../../button";
import { Icon, type MaterialSymbol } from "../../icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../tooltip";

export interface EditorButtonProps extends Omit<ButtonProps, "children"> {
  active?: boolean;
  icon?: MaterialSymbol;
  children?: ReactNode;
  tooltip?: string;
}

export const EditorButton: FC<EditorButtonProps> = ({
  active = false,
  icon,
  children,
  className,
  tooltip,
  ...props
}) => {
  const { editor } = useCurrentEditor();

  const isDisabled = useMemo(() => {
    if (!editor) {
      return true;
    }

    if (!editor.isEditable) {
      return true;
    }

    if (typeof props.disabled === "boolean") {
      return props.disabled;
    }

    return false;
  }, [editor, props.disabled]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          display={children ? "default" : "icon"}
          {...props}
          disabled={isDisabled}
          className={cn("font-normal", active ? "bg-foreground/10! text-foreground! filled" : "", className)}
        >
          {!!icon && <Icon icon={icon} />}
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" hidden={!tooltip}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};
